import { useMemo, useState } from 'react';
import { Botao } from '../../componentes/Botao.js';
import { CampoSelecao, CampoTexto } from '../../componentes/Campo.js';
import { DesenhoDeQr } from '../../componentes/DesenhoDeQr.js';
import { PainelLateral } from '../../componentes/PainelLateral.js';
import {
  montarQrDePedido,
  ORIGENS,
  type LinhaDoPedido,
  type OrigemDoPedido,
} from './qrDePedido.js';
import './NovoQrDePedido.css';

type Props = {
  readonly aoFechar: () => void;
};

const LINHA_VAZIA: LinhaDoPedido = {
  sku: '',
  derivacao: '',
  depositoColeta: '',
  depositoEntrega: '',
  quantidade: '',
};

const ROTULO_DA_ORIGEM: Record<OrigemDoPedido, string> = {
  PV: 'PV — pedido de venda',
  RQ: 'RQ — requisição eletrônica',
  OP: 'OP — componente de ordem de produção',
};

const OPCOES_DE_ORIGEM = ORIGENS.map((origem) => ({
  valor: origem,
  rotulo: ROTULO_DA_ORIGEM[origem],
}));

/** O formulário só cobra o que a pessoa começou a preencher. */
function linhaEmBranco(linha: LinhaDoPedido): boolean {
  const campos: readonly string[] = [
    linha.sku,
    linha.derivacao,
    linha.depositoColeta,
    linha.depositoEntrega,
    linha.quantidade,
  ];

  return campos.every((valor) => valor.trim() === '');
}

/**
 * Gerador do QR de coleta, a partir do painel (UC02, RF-010 a RF-012).
 *
 * A spec descreve o operador LENDO o QR do pedido, mas não diz quem o imprime —
 * e sem alguém imprimindo, o coletor não tem o que ler. Este painel é esse
 * alguém, enquanto a importação do documento direto do Senior (F2.4) não
 * existe.
 *
 * A validação NÃO é adorno: ela é o espelho das recusas do parser do coletor
 * (`mobile/src/coleta/qrDeColeta.ts`). Um QR impresso que o app recusa custa
 * uma viagem ao corredor com o papel na mão — por isso o desenho só aparece
 * quando o conteúdo passaria do outro lado, e o motivo da recusa fica à vista
 * enquanto não passa.
 *
 * O QR é desenhado no navegador (RNF-026): nenhum pedido do cliente sai daqui
 * para um serviço de imagem de terceiros.
 */
export function NovoQrDePedido({ aoFechar }: Props) {
  const [origem, definirOrigem] = useState<OrigemDoPedido>('PV');
  const [referencia, definirReferencia] = useState('');
  const [linhas, definirLinhas] = useState<readonly LinhaDoPedido[]>([LINHA_VAZIA]);

  const preenchidas = linhas.filter((linha) => !linhaEmBranco(linha));
  const comecou = referencia.trim() !== '' || preenchidas.length > 0;

  const resultado = useMemo(
    () => montarQrDePedido({ origem, referencia, linhas: preenchidas }),
    // `preenchidas` é derivado de `linhas` — depender das duas recalcularia à toa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [origem, referencia, linhas],
  );

  function alterarLinha(indice: number, campo: keyof LinhaDoPedido, valor: string) {
    definirLinhas((atuais) =>
      atuais.map((linha, posicao) => (posicao === indice ? { ...linha, [campo]: valor } : linha)),
    );
  }

  function adicionarLinha() {
    definirLinhas((atuais) => [...atuais, LINHA_VAZIA]);
  }

  function removerLinha(indice: number) {
    definirLinhas((atuais) => atuais.filter((_, posicao) => posicao !== indice));
  }

  const pronto = resultado.tipo === 'Pronto';
  const problemas = resultado.tipo === 'Recusado' ? resultado.problemas : [];

  return (
    <PainelLateral
      titulo="Novo QR de pedido"
      subtitulo="O coletor lê este código para abrir a coleta"
      aoFechar={aoFechar}
      rodape={
        <div className="qr-pedido__rodape">
          <Botao onClick={aoFechar}>Fechar</Botao>
          {pronto && (
            <Botao
              variante="primario"
              onClick={() => {
                window.print();
              }}
            >
              Imprimir
            </Botao>
          )}
        </div>
      }
    >
      <div className="qr-pedido">
        <div className="qr-pedido__cabecalho">
          <CampoSelecao
            rotulo="Origem"
            obrigatorio
            valor={origem}
            opcoes={OPCOES_DE_ORIGEM}
            aoMudar={(valor) => {
              definirOrigem(valor as OrigemDoPedido);
            }}
          />
          <CampoTexto
            rotulo="Referência"
            obrigatorio
            valor={referencia}
            aoMudar={definirReferencia}
            fonte="mono"
            ajuda={
              origem === 'OP'
                ? 'Ordem, etapa e sequência separadas por barra vertical: OP-99|10|3'
                : 'Número do documento, como PED-2024-8871'
            }
          />
        </div>

        <ol className="qr-pedido__linhas">
          {linhas.map((linha, indice) => (
            <li key={indice}>
              <fieldset className="qr-pedido__linha">
                <legend className="qr-pedido__legenda">Linha {indice + 1}</legend>

                <CampoTexto
                  rotulo="Produto"
                  obrigatorio
                  valor={linha.sku}
                  fonte="mono"
                  aoMudar={(valor) => {
                    alterarLinha(indice, 'sku', valor);
                  }}
                />
                <CampoTexto
                  rotulo="Derivação"
                  obrigatorio
                  valor={linha.derivacao}
                  fonte="mono"
                  ajuda="Obrigatória: o Senior recusa a linha sem ela"
                  aoMudar={(valor) => {
                    alterarLinha(indice, 'derivacao', valor);
                  }}
                />
                <CampoTexto
                  rotulo="Depósito de coleta"
                  valor={linha.depositoColeta}
                  fonte="mono"
                  ajuda="Código do depósito no Senior (ex.: ALM01), da filial do operador"
                  aoMudar={(valor) => {
                    alterarLinha(indice, 'depositoColeta', valor);
                  }}
                />
                <CampoTexto
                  rotulo="Depósito de entrega"
                  valor={linha.depositoEntrega}
                  fonte="mono"
                  ajuda="Código do depósito no Senior, da mesma filial"
                  aoMudar={(valor) => {
                    alterarLinha(indice, 'depositoEntrega', valor);
                  }}
                />
                <CampoTexto
                  rotulo="Quantidade"
                  obrigatorio
                  valor={linha.quantidade}
                  fonte="mono"
                  ajuda="Ponto como separador decimal: 350.20"
                  aoMudar={(valor) => {
                    alterarLinha(indice, 'quantidade', valor);
                  }}
                />

                {indice > 0 && (
                  <Botao
                    altura="pequena"
                    onClick={() => {
                      removerLinha(indice);
                    }}
                  >
                    Remover linha {indice + 1}
                  </Botao>
                )}
              </fieldset>
            </li>
          ))}
        </ol>

        <Botao altura="pequena" onClick={adicionarLinha}>
          Adicionar linha
        </Botao>

        {comecou && problemas.length > 0 && (
          <div className="qr-pedido__problemas" role="alert">
            <p className="qr-pedido__problemas-titulo">
              O coletor recusaria este QR. Corrija antes de imprimir:
            </p>
            <ul>
              {problemas.map((problema, indice) => (
                <li key={indice}>
                  {problema.linha === undefined ? '' : `Linha ${String(problema.linha)}: `}
                  {problema.mensagem}
                </li>
              ))}
            </ul>
          </div>
        )}

        {resultado.tipo === 'Pronto' && (
          <div className="qr-pedido__saida">
            <DesenhoDeQr
              conteudo={resultado.conteudo}
              descricao={`QR do pedido ${referencia.trim()}`}
              mensagemDeFalha="Este pedido tem linhas demais para caber em um QR. Divida em dois."
            />
            <p className="qr-pedido__rotulo-conteudo">Conteúdo do código, para conferência:</p>
            <p className="qr-pedido__conteudo">{resultado.conteudo}</p>
          </div>
        )}
      </div>
    </PainelLateral>
  );
}
